// pages/AppRemitocReactTable.jsx
import React, { useState, useEffect, useMemo, useCallback } from "react";
import styled from "styled-components";
import { useFilters, useSortBy, usePagination } from "react-table";
import { getRemitocs, deleteRemitoc, getRemitocById } from "../helpers/rutaRemitocs";
import Table from "./TableContainer";
import ModalRemitoc from "../components/ModalFormRemitoc";
// import { generarPDFRemito } from "../helpers/pdfRemitos"; // si tenés un PDF propio, cambiá por pdfRemitoc
import { generarPDFRemitoc } from "../helpers/pdfRemitocs";

const Styles = styled.div`
  .table-container { overflow: auto; height: 600px; }
  table { border: 1px solid black; border-spacing: 0; font-size: 13px; width: 100%; }
  th, td { padding: 0.5rem; border-bottom: 1px solid black; border-right: 1px solid black; }
  th { background-color: #778899; position: sticky; top: 0; z-index: 3; text-align: center; font-size: 12px; }
  td { background-color: inherit; }

  /* Si la fila tiene un marcador __colorFlag=1, pintá todos sus td */
  tr:has(.__colorFlag[data-flag="1"]) td {
    background-color: rgba(149, 171, 177, 1) !important; /* ajustá el color luego */
  }


  .row-colored,
  .row-colored td { background-color: #a1a19eff !important; }

  .pagination { padding: 0.5rem; background-color: #778899; font-size: 15px; font-weight: bold; }
  .action-btn { border: none; background: transparent; cursor: pointer; }
  .action-btn--edit { color: #0d6efd; }
  .action-btn--del  { color: #dc3545; }

  /* Sticky horizontal del header para las 3 primeras columnas (Edita / Borra / PDF) */
  .table-container thead th:nth-child(1) { position: sticky; left: 0; z-index: 6; }
  .table-container thead th:nth-child(2) { position: sticky; left: 50px; z-index: 6; }
  .table-container thead th:nth-child(3) { position: sticky; left: 100px; z-index: 6; }
`;

const fmtMoney = (n) =>
  Number(n || 0).toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const HILITE = "#fff8d1"; // color suave para filas con tilde = true

export default function AppRemitocReactTable() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const [remitocSel, setRemitocSel] = useState(null);
  const [showModalEdit, setShowModalEdit] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await getRemitocs();
      const list = Array.isArray(resp) ? resp : (resp?.remitosc || []);
      list.sort(
        (a, b) =>
          (b.codrem || 0) - (a.codrem || 0) || new Date(b.fecha) - new Date(a.fecha)
      );
      setData(list);
    } catch (e) {
      console.error("❌ Remitos C:", e);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCloseEdit = () => {
    setShowModalEdit(false);
    setRemitocSel(null);
    setTimeout(() => fetchData(), 0);
  };

  const borrar = useCallback(async (id) => {
    if (!window.confirm("¿Eliminar (lógico) este Remito C?")) return;
    try {
      await deleteRemitoc(id);
      fetchData();
    } catch (e) {
      console.error("❌ deleteRemitoc:", e);
    }
  }, [fetchData]);

  const abrirEditar = useCallback(async (row) => {
    try {
      setShowModalEdit(true);
      setRemitocSel(null);
      const resp = await getRemitocById(row.original._id);
      if (resp?.ok) setRemitocSel(resp.remitoc);
      else alert(resp?.error || "No se pudo cargar el Remito C.");
    } catch (e) {
      console.error(e);
      alert("Error cargando el Remito C.");
    }
  }, []);

  const pdfRemitoC = useCallback((id) => {
    generarPDFRemitoc(id, {
      // como el archivo está en /public/SanPancracio.png
      logoUrl: `${window.location.origin}/SanPancracio.png`,
      // también podrías usar directamente: logoUrl: "/SanPancracio.png",
      empresa: {
        nombre: "SAN PANCRACIO SRL",
        direccion: "Avda. Peron 1600 - Oficina 29 Piso 1 -Yerba Buena - Tucumán",
        telefono: "Tel: 0381-3008829",
        email: "contacto@sanpancracio.com",
      },
    });
  }, []);

  const columns = useMemo(() => [
    // Acciones
    {
      id: "edit",
      Header: "Edita",
      accessor: "_id",
      width: 50,
      Cell: ({ row }) => (
        <button
          className="action-btn action-btn--edit"
          title="Editar Remito C"
          onClick={() => abrirEditar(row)}
        >
          <i className="fa fa-pencil" aria-hidden="true" />
        </button>
      ),
    },
    {
      id: "del",
      Header: "Borra",
      accessor: "_id",
      width: 50,
      Cell: ({ row }) => (
        <button
          className="action-btn action-btn--del"
          title="Borrar Remito C"
          onClick={() => borrar(row.original._id)}
        >
          <i className="fa fa-trash" aria-hidden="true" />
        </button>
      ),
    },
    {
      id: "pdf",
      Header: "",
      accessor: "_id",
      width: 50,
      Cell: ({ row }) => (
        <button
          className="action-btn action-btn--pdf"
          title="Descargar Remito C"
          onClick={() => pdfRemitoC(row.original._id)}
        >
          <i className="fa fa-file-pdf-o" aria-hidden="true" />
        </button>
      ),
    },


    // Flags al frente
    {
      Header: "Tilde",
      id: "tilde",
      accessor: (r) => (r.tilde ? "✅" : "—"),
      width: 60,
      Cell: ({ row, value }) => (
        <div style={{ textAlign: "center" }}>
          <span className="__colorFlag" data-flag={row.original?.color ? "1" : "0"}>
            {value}
          </span>
        </div>
      ),
    },
    {
      Header: "Color",
      id: "color",
      accessor: (r) => (r.color ? "✅" : "—"),
      width: 60,
      Cell: ({ value }) => <div style={{ textAlign: "center" }}>{value}</div>,
    },

    // Datos principales
    { Header: "Remito", 
      accessor: "codrem", 
      width: 60,
      Cell: ({ value }) => <div style={{ textAlign: "center" }}>{value}</div>,
     },
    {
      Header: "Fecha",
      accessor: (r) => (r.fecha ? new Date(r.fecha) : ""),
      id: "fecha",
      width: 110,
      Cell: ({ value }) => 
        value instanceof Date ? value.toLocaleDateString("es-AR") : "-",
    },
    {
      Header: "Hora",
      accessor: "horaCarga",
      id: "horaCarga",
      width: 80,
      Cell: ({ value }) => (
        <div style={{ textAlign: "center" }}>
          {value || "-"}
        </div>
      ),
    },
    { Header: "Proveedor", accessor: "proveedor.razonsocial", width: 240 },
    { Header: "Cliente", accessor: "cliente.razonsocial", width: 220 },
    { Header: "Cliente Real", accessor: "clientereal.razonsocial", width: 220 },
    { Header: "Transporte", accessor: "transporte.transporte", width: 180 },

    // Nuevos
    { Header: "Chofer", accessor: "chofer.nombre", width: 180 },
    {
      Header: "Lote",
      id: "lote",
      width: 170,
      accessor: (r) =>
        r.lote
          ? (r.lote.codlote != null ? `#${r.lote.codlote} - ${r.lote.lote}` : r.lote.lote || "")
          : "",
      Cell: ({ value }) => <div style={{ textAlign: "left" }}>{value || "-"}</div>,
    },

    // Kilos
    {
      Header: "KG Campo",
      accessor: "kgCampo",
      id: "kgCampo",
      width: 100,
      Cell: ({ value }) => (
        <div style={{ textAlign: "right" }}>
          {Number(value || 0).toLocaleString("es-AR")}
        </div>
      ),
      Footer: ({ rows }) => {
        const total = rows.reduce((s, r) => s + (r.values.kgCampo || 0), 0);
        return (
          <div style={{ textAlign: "right", fontWeight: "bold", color: "blue" }}>
            {total.toLocaleString("es-AR")}
          </div>
        );
      },
    },
    {
      Header: "KG Recib.",
      accessor: "kgRecibido",
      id: "kgRecibido",
      width: 100,
      Cell: ({ value }) => (
        <div style={{ textAlign: "right" }}>
          {Number(value || 0).toLocaleString("es-AR")}
        </div>
      ),
      Footer: ({ rows }) => {
        const total = rows.reduce((s, r) => s + (r.values.kgRecibido || 0), 0);
        return (
          <div style={{ textAlign: "right", fontWeight: "bold", color: "blue" }}>
            {total.toLocaleString("es-AR")}
          </div>
        );
      },
    },
    {
      Header: "Dif",
      accessor: "diferencia",
      id: "diferencia",
      width: 90,
      Cell: ({ value }) => (
        <div style={{ textAlign: "right" }}>
          {Number(value || 0).toLocaleString("es-AR")}
        </div>
      ),
      Footer: ({ rows }) => {
        const total = rows.reduce((s, r) => s + (r.values.diferencia || 0), 0);
        return (
          <div style={{ textAlign: "right", fontWeight: "bold", color: "blue" }}>
            {total.toLocaleString("es-AR")}
          </div>
        );
      },
    },

    // Precios y totales
    // {
    //   Header: "Precio Compra",
    //   accessor: "precioCompra",
    //   id: "precioCompra",
    //   width: 120,
    //   Cell: ({ value }) => <div style={{ textAlign: "right" }}>{fmtMoney(value)}</div>,
    // },
    // {
    //   Header: "Precio Venta",
    //   accessor: "precioVenta",
    //   id: "precioVenta",
    //   width: 120,
    //   Cell: ({ value }) => <div style={{ textAlign: "right" }}>{fmtMoney(value)}</div>,
    // },
    // {
    //   Header: "Total Compra",
    //   accessor: "totalCompra",
    //   id: "totalCompra",
    //   width: 130,
    //   Cell: ({ value }) => <div style={{ textAlign: "right" }}>{fmtMoney(value)}</div>,
    //   Footer: ({ rows }) => {
    //     const total = rows.reduce((s, r) => s + (r.values.totalCompra || 0), 0);
    //     return (
    //       <div style={{ textAlign: "right", fontWeight: "bold", color: "red" }}>
    //         {fmtMoney(total)}
    //       </div>
    //     );
    //   },
    // },
    // {
    //   Header: "Total Venta",
    //   accessor: "totalVenta",
    //   id: "totalVenta",
    //   width: 130,
    //   Cell: ({ value }) => <div style={{ textAlign: "right" }}>{fmtMoney(value)}</div>,
    //   Footer: ({ rows }) => {
    //     const total = rows.reduce((s, r) => s + (r.values.totalVenta || 0), 0);
    //     return (
    //       <div style={{ textAlign: "right", fontWeight: "bold", color: "red" }}>
    //         {fmtMoney(total)}
    //       </div>
    //     );
    //   },
    // },
    // {
    //   Header: "Comisión",
    //   accessor: "comision",
    //   id: "comision",
    //   width: 120,
    //   Cell: ({ value }) => <div style={{ textAlign: "right" }}>{fmtMoney(value)}</div>,
    //   Footer: ({ rows }) => {
    //     const total = rows.reduce((s, r) => s + (r.values.comision || 0), 0);
    //     return (
    //       <div
    //         style={{
    //           textAlign: "right",
    //           fontWeight: "bold",
    //           color: total < 0 ? "red" : "green",
    //         }}
    //       >
    //         {fmtMoney(total)}
    //       </div>
    //     );
    //   },
    // },

    // Otros comunes
    { Header: "Nro DTV Compra", accessor: "nroDTVCompra", width: 150 },
    { Header: "Nro DTV Venta", accessor: "nroDTVVenta", width: 150 },
    { Header: "Variedad", accessor: "variedad", width: 180 },

    // Extras RemitoC
    { Header: "Remito", accessor: "remitoint", width: 80 },
    { Header: "Estado", accessor: "estado.estado", width: 120 },
    { Header: "Observación", accessor: "observa.observa", width: 300 },
    { Header: "Factura Compra", accessor: "factcompra", width: 150 },
    { Header: "Factura Venta", accessor: "factventa", width: 150 },

    { Header: "Notas", accessor: "nota", width: 600 },
  ], [abrirEditar, borrar, pdfRemitoC]);

  return (
    <div className="container mt-4">
      <h3 className="text-center mb-4">LISTADO DE REMITOS C</h3>

      <Styles>
        <div className="table-container">
          <Table
            columns={columns}
            data={data}
            loading={loading}
            plugins={[useFilters, useSortBy, usePagination]}
            /* 🔽 Resaltado por tilde */
            getRowProps={(row) => (
              row.original?.color ? { className: "row-colored" } : {}
            )}
          />
        </div>
      </Styles>

      <ModalRemitoc
        show={showModalEdit}
        remitoc={remitocSel}
        onClose={handleCloseEdit}
      />
    </div>
  );
}
